import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import { postFetcher } from "api/swr";
import { MyFormDatePicker, MyFromTimePicker } from "components/ui/MyFormField";
import useSWRMutation from "swr/mutation";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { LapTimerIcon } from "@radix-ui/react-icons";

const ScheduleFormSchema = z.object({
  date: z.date({
    required_error: "Date is required",
  }),
  time: z.string({
    required_error: "Time is required",
  }),
});

type DraftPublishButtonProps = {
  draftId: string | undefined;
};

/**
 * POST /spine-frames/:id/publish — empty body = publish now; `{ onlineTime }` = scheduled publish.
 */
export function DraftPublishButton({ draftId }: DraftPublishButtonProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof ScheduleFormSchema>>({
    resolver: zodResolver(ScheduleFormSchema),
  });

  const { trigger, isMutating } = useSWRMutation(
    draftId ? ["spine-frames-publish", draftId] : null,
    async (
      _key: [string, string],
      { arg }: { arg?: { onlineTime?: number } },
    ) => {
      if (!draftId) {
        throw new Error("No draftId");
      }
      return postFetcher(`/spine-frames/${draftId}/publish`, {
        arg: arg ?? {},
      });
    },
    {
      onSuccess: (data) => {
        setOpen(false);
        form.reset();
        const isScheduled = data && (data as { message?: string }).message === "scheduled";
        toast({
          title: isScheduled ? "Publish scheduled" : "Published",
          className: "bg-green-500 text-white",
        });
        if (!isScheduled) {
          void navigate("/admin/spine?spineTab=draft");
        }
      },
      onError: (err: unknown) => {
        toast({
          variant: "destructive",
          title: "Request failed",
          description: err instanceof Error ? err.message : String(err),
        });
      },
    },
  );

  const runOnlineNow = () => {
    if (!draftId || isMutating) return;
    void trigger({});
  };

  const onSubmitSchedule = (values: z.infer<typeof ScheduleFormSchema>) => {
    if (!draftId || isMutating) return;
    const date = dayjs(values.date).format("YYYY-MM-DD");
    const onlineTime = new Date(`${date} ${values.time}:00`).getTime();
    void trigger({ onlineTime });
  };

  return (
    <>
      <Button type="button" onClick={() => setOpen(true)} disabled={!draftId}>
        Publish
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Publish</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmitSchedule)}
              className="space-y-4"
            >
              <MyFormDatePicker
                form={form}
                name="date"
                label="Date"
                description=""
              />
              <MyFromTimePicker
                form={form}
                name="time"
                label="Time"
                className=""
                description=""
              />
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Button
                  type="submit"
                  disabled={isMutating || !draftId}
                  className="gap-2"
                >
                  <LapTimerIcon className="h-4 w-4" />
                  {isMutating ? "Submitting…" : "Schedule publish"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={isMutating || !draftId}
                  onClick={runOnlineNow}
                >
                  Online Now
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
